import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { format } from "date-fns";
import { ArrowRight } from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import LazyImage from "./LazyImage";
import NewsCardSkeleton from "./skeletons/NewsCardSkeleton";

interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  featured_image_url: string | null;
  category: string;
  published_at: string | null;
  created_at: string;
  content: string | null;
}

const NewsSection = () => {
  const { settings } = useSiteSettings();
  const newsEnabled = settings.news_enabled !== "false";
  const { data: articles, isLoading } = useQuery({
    queryKey: ["latest-news"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_articles")
        .select("id, title, slug, featured_image_url, category, published_at, created_at, content")
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(3);

      if (error) throw error;
      return data as NewsArticle[];
    },
  });

  const getExcerpt = (content: string | null, maxLength: number = 100) => {
    if (!content) return "";
    const stripped = content.replace(/<[^>]*>/g, "").trim();
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength).trim() + "...";
  };

  if (!newsEnabled) return null;

  if (isLoading) {
    return (
      <section id="news" className="py-20 md:py-32 section-dark">
        <div className="container mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div className="section-header mb-0">
              <span className="section-label">Stay Updated</span>
              <h2 className="section-title text-dark-foreground">Latest News</h2>
              {/* <p className="section-subtitle text-[#555]">Behind the scenes, events, and the road to release.</p> */}
            </div>
          </div>

          {/* Skeleton Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <NewsCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!articles || articles.length === 0) {
    return null;
  }

  return (
    <section id="news" className="py-20 md:py-32 section-dark">
      <div className="mx-auto px-6 md:px-12">
        {/* Header */}
        <AnimatedSection
          animation="fade-up"
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8"
        >
          <div className="section-header mb-0">
            <span className="section-label">Stay Updated</span>
            <h2 className="section-title text-dark-foreground">Latest News</h2>
            {/* <p className="section-subtitle text-[#555]">Announcements, events, and community highlights</p> */}
          </div>
          <Link
            to="/news"
            className="btn-outline-light group"
          >
            View All
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </AnimatedSection>

        {/* News Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article, index) => (
            <AnimatedSection key={article.id} animation="fade-up" delay={index * 100}>
              <Link
                to={`/news/${article.slug}`}
                className="group block border border-border bg-card overflow-hidden transition-all duration-500 hover:border-blood-red hover:-translate-y-1 flex flex-col min-h-[600px]"
              >
                {/* Image Container */}
                <div className="relative h-[350px] flex-shrink-0 overflow-hidden">
                  {article.featured_image_url ? (
                    <LazyImage
                      src={article.featured_image_url}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground">No image</div>
                  )}
                </div>

                {/* Content */}
                <div className="p-8 flex flex-col flex-1 min-h-0">
                  {/* Meta */}
                  <div className="flex items-center gap-3 text-xs mb-4">
                    <span className="text-blood-red font-semibold tracking-wider uppercase">{article.category}</span>
                    <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                    <span className="text-muted-foreground">
                      {format(new Date(article.published_at || article.created_at), "MMM d, yyyy")}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-display font-bold text-card-foreground group-hover:text-blood-red transition-colors duration-300 mb-3 leading-tight">
                    {article.title}
                  </h3>

                  {/* Excerpt */}
                  {article.content && (
                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2">
                      {getExcerpt(article.content, 120)}
                    </p>
                  )}
                </div>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
